import { For, Show, Signal, createEffect, createSignal } from 'solid-js'
import { debounce, nbsp } from 'common';
import Button from '../../st-button/st-button';
import Icon from '../../st-icon/st-icon';
import Dropdown from '../../st-dropdown/st-dropdown';
import Flexbox from '../../st-flexbox/st-flexbox';
import STInput from '../../st-input/st-input';
import { StaffSearchResult, visitorCheckin as api } from 'st-app/api';

import fkStyles from '../../st-fk-input/st-fk-input.module.css'
import inputStyles from '../../st-input/st-input.module.css'
import comboStyles from '../../st-combobox/st-combobox.module.css'

export type StaffSearchInputProps = {
    value: Signal<string>;
    displayName: Signal<string>;
}

export default function StaffSearchInput(props: StaffSearchInputProps) {
    const [staffGU, setStaffGU] = props.value;
    const [displayName, setDisplayName] = props.displayName;

    const [search, setSearch] = createSignal('');
    const [results, setResults] = createSignal<StaffSearchResult[]>([]);
    const [highlightedIndex, setHighlightedIndex] = createSignal(-1);
    const [showDropdown, setShowDropdown] = createSignal(false);
    const [contRef, setContRef] = createSignal<HTMLDivElement>();

    const find = debounce((s: string) => {
        if (!s) {
            setResults([]);
            setHighlightedIndex(-1);
        } else {
            api.SearchStaff(s).then(r => {
                setResults(r);
                setHighlightedIndex(-1);
            });
        }
    }, 500);

    createEffect(() => find(search()));

    function select(item: StaffSearchResult) {
        setStaffGU(item.staffGU ?? '');
        setDisplayName(item.formattedName ?? '');
        setShowDropdown(false);
        setSearch('');
        setHighlightedIndex(-1);
    }

    function clearValue() {
        setStaffGU('');
        setDisplayName('');
        setResults([]);
        setHighlightedIndex(-1);
        setShowDropdown(false);
        setSearch('');
    }

    function handleKeyDown(e: KeyboardEvent) {
        const items = results();
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setHighlightedIndex(i => Math.min(i + 1, items.length - 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setHighlightedIndex(i => Math.max(i - 1, 0));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            const idx = highlightedIndex();
            const target = idx >= 0 ? items[idx] : items[0];
            if (target) select(target);
        }
    }

    return (
        <>
            <Flexbox
                ref={setContRef}
                middle
                dir="horizontal"
                class={`${fkStyles.value} ${inputStyles.input}`}
                style="padding: 2px 6px; cursor: pointer;"
                onClick={() => setShowDropdown(true)}
            >
                <Flexbox.Item auto style="overflow: hidden; white-space: nowrap; text-overflow: ellipsis; pointer-events: none;">
                    {displayName() || nbsp}
                </Flexbox.Item>
                <i class="fas fa-link" style={{ "font-size": '15px', "padding": "4px" }} />
            </Flexbox>

            <Dropdown open={[showDropdown, setShowDropdown]} placement='bottom-start' target={contRef()}>
                <Dropdown.Content>
                    <div onKeyDown={handleKeyDown}>
                        <STInput focused value={[search, setSearch]} icon="fas fa-search" />
                        <div class={comboStyles.combobox} role="listbox">
                            <Show
                                when={results().length > 0}
                                fallback={
                                    <Show when={search()}>
                                        <div style="padding: 6px 12px;"><i>No Results</i></div>
                                    </Show>
                                }
                            >
                                <For each={results()}>{(item, idx) =>
                                    <div
                                        class={comboStyles.item}
                                        role="listitem"
                                        style={highlightedIndex() === idx() ? 'background-color: var(--st-menu-bg-hover);' : ''}
                                        onClick={() => select(item)}
                                    >
                                        {item.formattedName}
                                    </div>
                                }</For>
                            </Show>
                        </div>
                        <div style="display: flex; justify-content: flex-end;">
                            <Button type="link" onClick={clearValue}>clear</Button>
                        </div>
                    </div>
                </Dropdown.Content>
            </Dropdown>
        </>
    );
}
